import React, { useRef } from "react";
import { useDroppable, useDndMonitor, Active } from "@dnd-kit/core";
import { APP_IDS, StringID } from "userful-chronos-app-common-js/dist/models/common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { DnD_TYPES } from "./DnDUtils";
import DestinationDraggable, { DestinationDraggableData } from "./DestinationDraggable";
import {
    MappingGroupDestination,
    DestinationType,
    EMPTY_DESTINATION_IDS,
    DestinationsFilter,
    AnyDestination,
    isEdgeMapper,
    EdgeMapperComp,
    AnyMapper,
    MixerMapperComp,
} from "userful-chronos-app-common-js/dist/models/mapping/MappingGroups";
import {
    buildMappingGroupDestinations,
    getRemoveDestinations,
    getUsage,
} from "userful-chronos-app-common-js/dist/models/cdm/DestinationsUtils";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
    useMappingSelector,
    useMappingDispatch,
    mappingGroupsSliceActions,
} from "userful-chronos-common-store/dist/mapping";
import { sortByName } from "userful-chronos-common-store/dist/destinations";
import DestinationDropInUseConfirmation from "./DestinationDropInUseConfirmation";
import UserfulSearchBar from "../../Components/searchBar/UserfulSearchBar";
import "./../../Components/card/UserfulInfinityCard.scss";
import { faCircleNotch } from "@fortawesome/pro-solid-svg-icons";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { hasClearanceLevelForAction } from "userful-chronos-app-common-js/dist/authenticataion/roles";
import { UserfulSecurityData } from "userful-chronos-app-common-js/dist/models/common";
import { DestinationIDSet } from "userful-chronos-app-common-js/dist/models/cdm/CdmGroup";
import MirrorError from "./errors/MirrorError";
import DestError from "./errors/DestError";
import { AlertInterfaces } from "userful-chronos-common-store/dist/notifications";
import { useVideoEngineSelector } from "userful-chronos-common-store/dist/videoengines";
import HLSAudioWarning from "./errors/HLSAudioWarning";

interface Props {
    mappingGroupID?: StringID;
    alert?: AlertInterfaces.AlertConfig;
    onDestinationsIDSetChange?: (destinationIDSet: DestinationIDSet) => void;
    expendedDropZone?: boolean;
    dragging?: boolean;
    appID?: string;
    disableMirroring?: boolean;
    destinationsFilter?: DestinationsFilter;
    onDragEndCallback?: (event: any) => void;
    onDestinationRemovedCallback?: () => void;
    canExecuteMapper?: string;
    isNotification?: boolean;
}

const typeMatches = (active: Active) => active.data.current.draggingType === DnD_TYPES.destination;
export default function DestinationsDropZone({
    mappingGroupID,
    alert,
    dragging,
    onDestinationsIDSetChange,
    expendedDropZone,
    ...props
}: Props) {
    const { t } = useTranslation();

    const ref = useRef(null);
    const [refresh, setRefresh] = useState(false);

    const globalStates = getGlobalStates();

    const mappingGroups = useMappingSelector((state) => state.mappingGroupStore.mappingGroups);
    const areBusy = useMappingSelector((state) => state.mappingGroupStore.busyMapper);

    const videoEngines = useVideoEngineSelector((state) => state.videoEngineStore.videoEngines);
    const veoReady = videoEngines.findIndex((v) => v.veoStatus === "ACTIVE") >= 0;
    let mappingGroup: AnyMapper = undefined;
    let isBusy = false;
    if (mappingGroupID) {
        mappingGroup = mappingGroups.find((group) => group.id.value === mappingGroupID.value);
        isBusy = areBusy.find((item) => item.id.value === mappingGroupID.value) ? true : false;
    }

    const pendingMoveDestinations = useMappingSelector(
        (state) => state.mappingGroupStore.pendingMoveDestinationsRequest,
    );

    const groups = [];
    const videoWalls = useMappingSelector((state) => state.videowallsStore.videowallDestinations);
    const displays = useMappingSelector((state) => state.displaysStore.displayDestinations);
    const physicalVideo = sortByName([...displays]);
    const physicalAudio = useMappingSelector((state) => state.audiosStore.audioDestinations);

    const isDestValid = props.destinationsFilter || (() => true);

    const dispatch = useMappingDispatch();
    const edgeMapperContentValid = (data: { item: AnyDestination; type: keyof DestinationIDSet }): boolean => {
        if (!isEdgeMapper(mappingGroup)) {
            return true;
        }
        const edgeMapper = mappingGroup as EdgeMapperComp;
        const insetType = edgeMapper.inset?.playList.items[0]?.source.type.value;
        return data.type === "physicalVideo" || insetType !== "Web_Browser";
    };

    const [existingDestinations, setExistingDestinations] = useState<MappingGroupDestination[]>([]);
    const [currentDestinations, setCurrentDestinations] = useState([]);
    const [input, setInput] = useState("");
    const [pendingAddDestination, setPendingAddDestination] = useState(null);
    const [pendingHLSAudioDestination, setPendingHLSAudioDestination] = useState(null);
    const [destError, setDestError] = useState<string>(null);

    const { active, isOver, setNodeRef } = useDroppable({
        id: `destination-dropzone-${mappingGroupID ? mappingGroupID.value : alert.id.value}`,
        data: {
            draggingType: DnD_TYPES.destination,
        },
    });

    const canExecute = (securityData: UserfulSecurityData) => {
        return hasClearanceLevelForAction(
            securityData,
            globalStates.userData.userID,
            globalStates.userPermission,
            globalStates.actions,
            "execute",
        );
    };

    useEffect(() => {
        if (mappingGroup) {
            setExistingDestinations(
                buildMappingGroupDestinations(groups, physicalVideo, physicalAudio, videoWalls, mappingGroup, displays),
            );
        } else {
            if (alert) {
                const dest: MappingGroupDestination[] = [];

                for (let i = 0; i < alert.destinationIDSet.groups.length; i++) {
                    const group = groups.find((g) => g.id.value === alert.destinationIDSet.groups[i].value);
                    if (group) {
                        dest.push({
                            data: group,
                            destinationType: "groups",
                        });
                    }
                }

                for (let i = 0; i < alert.destinationIDSet.physicalAudio.length; i++) {
                    const group = physicalAudio.find(
                        (g) => g.id.value === alert.destinationIDSet.physicalAudio[i].value,
                    );
                    if (group) {
                        dest.push({
                            data: group,
                            destinationType: "physicalAudio",
                        });
                    }
                }

                for (let i = 0; i < alert.destinationIDSet.physicalVideo.length; i++) {
                    const group = physicalVideo.find(
                        (g) => g.id.value === alert.destinationIDSet.physicalVideo[i].value,
                    );
                    if (group) {
                        dest.push({
                            data: group,
                            destinationType: "physicalVideo",
                        });
                    }
                }

                for (let i = 0; i < alert.destinationIDSet.videoWalls.length; i++) {
                    const group = videoWalls.find((g) => g.id.value === alert.destinationIDSet.videoWalls[i].value);
                    if (group) {
                        dest.push({
                            data: group,
                            destinationType: "videoWalls",
                        });
                    }
                }

                setExistingDestinations(dest);
            }
        }
    }, [mappingGroup, videoWalls, displays, physicalAudio, alert]);

    useEffect(() => {
        if (input.length > 0) {
            const destinationsArray = [];

            for (let i = 0; i < existingDestinations.length; i++) {
                const name = existingDestinations[i].data.name.toLowerCase();

                if (name.includes(input.toLowerCase())) destinationsArray.push(existingDestinations[i]);
            }

            setCurrentDestinations(destinationsArray);
        } else {
            setCurrentDestinations(existingDestinations);
        }
    }, [existingDestinations]);

    const isDraggingValid = isOver && typeMatches(active);

    useDndMonitor({
        onDragEnd(event) {
            const { active } = event;

            if (!veoReady) {
                return;
            }
            if (mappingGroupID) {
                // adding the condition if this mapper is executable, if  canExecuteMapper is not provided and if provided it must be true}
                // also if the its the spaces app then user should be admin
                if (
                    (props.canExecuteMapper === "canExecute" || !props.canExecuteMapper) &&
                    (props.appID !== APP_IDS.Spaces ||
                        (props.appID === APP_IDS.Spaces && globalStates.userPermission.isSapphireAdmin))
                ) {
                    if (
                        isDraggingValid &&
                        !isBusy &&
                        (canExecute(active.data.current.item.userfulSecurityData) || props.isNotification)
                    ) {
                        const currentData = active.data.current as DestinationDraggableData;
                        if (
                            existingDestinations.findIndex(
                                (item) => item.data.id.value === currentData.item.id.value,
                            ) >= 0
                        ) {
                            // destination already exists;
                            return;
                        }
                        if (currentData.item) {
                            const existingVideoDestinations = existingDestinations.filter(
                                (d) => d.destinationType !== "physicalAudio",
                            );
                            const checkData = {
                                item: currentData.item,
                                type: currentData.destinationType,
                            };
                            if (!isDestValid(checkData)) {
                                setDestError(t("CommonUI.MappingGroups.destErrorDescription"));
                                return;
                            } else if (!edgeMapperContentValid(checkData)) {
                                setDestError(t("CommonUI.MappingGroups.destErrorDescription2"));
                                return;
                            }
                            const tryingToMirrorVideoDest =
                                props.disableMirroring &&
                                currentData.destinationType !== "physicalAudio" &&
                                existingVideoDestinations.length > 0;

                            const removeDestinations = getRemoveDestinations(
                                tryingToMirrorVideoDest,
                                currentData.item,
                                currentData.destinationType,
                                existingDestinations,
                            );
                            const destinations = { ...EMPTY_DESTINATION_IDS };
                            destinations[currentData.destinationType] = [currentData.item.id];
                            const destinationInUse = !!getUsage(currentData.item, mappingGroup);
                            const addRequest = {
                                toMappingGroupID: mappingGroupID,
                                fromMappingGroupID: currentData.fromMappingGroup, // enable this field to 'move' destinations instead of adding
                                destinations,
                                destinationInUse,
                                removeDestinations,
                            };
                            if (tryingToMirrorVideoDest) {
                                setPendingAddDestination(addRequest);
                                return;
                            }
                            const audioDestInHLSMapper =
                                mappingGroup &&
                                !isEdgeMapper(mappingGroup) &&
                                (mappingGroup as MixerMapperComp).destinationStreamType === "HLS" &&
                                currentData.destinationType === "physicalAudio";

                            if (audioDestInHLSMapper) {
                                setPendingHLSAudioDestination(addRequest);
                                return;
                            }

                            if (props.onDragEndCallback) {
                                props.onDragEndCallback(event);
                            }
                            dispatch(mappingGroupsSliceActions.addOrUpdateDestinationsToServer(addRequest));
                        }
                    }
                }
            } else {
                if (isDraggingValid) {
                    const currentData = active.data.current as DestinationDraggableData;
                    if (
                        existingDestinations.findIndex((item) => item.data.id.value === currentData.item.id.value) >= 0
                    ) {
                        // destination already exists;
                        return;
                    }
                    if (currentData.item) {
                        const destinationIDSet = { groups: [], physicalAudio: [], physicalVideo: [], videoWalls: [] };

                        destinationIDSet.groups = [...alert.destinationIDSet.groups];
                        destinationIDSet.physicalAudio = [...alert.destinationIDSet.physicalAudio];
                        destinationIDSet.physicalVideo = [...alert.destinationIDSet.physicalVideo];
                        destinationIDSet.videoWalls = [...alert.destinationIDSet.videoWalls];

                        if (currentData.destinationType === "groups") {
                            destinationIDSet.groups.push(currentData.item.id);
                        } else if (currentData.destinationType === "physicalAudio") {
                            destinationIDSet.physicalAudio.push(currentData.item.id);
                        } else if (currentData.destinationType === "physicalVideo") {
                            destinationIDSet.physicalVideo.push(currentData.item.id);
                        } else if (currentData.destinationType === "videoWalls") {
                            destinationIDSet.videoWalls.push(currentData.item.id);
                        }

                        onDestinationsIDSetChange(destinationIDSet);
                    }
                }
            }
        },
    });

    const handleRemoveDestination = (destinationID: StringID, destinationType: DestinationType) => {
        if (mappingGroupID) {
            const destinations = { ...EMPTY_DESTINATION_IDS };
            destinations[destinationType] = [destinationID];
            if (props.onDestinationRemovedCallback) {
                props.onDestinationRemovedCallback();
            }
            dispatch(
                mappingGroupsSliceActions.removeDestinationToServer({
                    mappingGroupID: mappingGroupID,
                    destinations,
                }),
            );
        } else {
            const destinationIDSet = { groups: [], physicalAudio: [], physicalVideo: [], videoWalls: [] };

            destinationIDSet.groups = [...alert.destinationIDSet.groups];
            destinationIDSet.physicalAudio = [...alert.destinationIDSet.physicalAudio];
            destinationIDSet.physicalVideo = [...alert.destinationIDSet.physicalVideo];
            destinationIDSet.videoWalls = [...alert.destinationIDSet.videoWalls];

            if (destinationType === "groups") {
                destinationIDSet.groups = destinationIDSet.groups.filter((id) => id.value !== destinationID.value);
            } else if (destinationType === "physicalAudio") {
                destinationIDSet.physicalAudio = destinationIDSet.physicalAudio.filter(
                    (id) => id.value !== destinationID.value,
                );
            } else if (destinationType === "physicalVideo") {
                destinationIDSet.physicalVideo = destinationIDSet.physicalVideo.filter(
                    (id) => id.value !== destinationID.value,
                );
            } else if (destinationType === "videoWalls") {
                destinationIDSet.videoWalls = destinationIDSet.videoWalls.filter(
                    (id) => id.value !== destinationID.value,
                );
            }

            onDestinationsIDSetChange(destinationIDSet);
        }
    };

    const onDestinationSearchBarChange = (input) => {
        setInput(input);

        const destinationsArray = [];

        for (let i = 0; i < existingDestinations.length; i++) {
            const name = existingDestinations[i].data.name.toLowerCase();

            console.log(name);
            if (name.includes(input.toLowerCase())) destinationsArray.push(existingDestinations[i]);
        }

        setCurrentDestinations(destinationsArray);
    };

    const hideMirrorError = () => setPendingAddDestination(false);
    const confirmMirrorReplace = () => {
        if (!pendingAddDestination) {
            return;
        }
        dispatch(mappingGroupsSliceActions.addOrUpdateDestinationsToServer(pendingAddDestination));
        hideMirrorError();
    };

    const hideHLSAudioError = () => setPendingHLSAudioDestination(false);
    const confirmHLSAudio = () => {
        if (!pendingHLSAudioDestination) {
            return;
        }
        dispatch(mappingGroupsSliceActions.addOrUpdateDestinationsToServer(pendingHLSAudioDestination));
        hideHLSAudioError();
    };

    // console.log(ref.current?.scrollWidth, ref.current?.clientWidth);

    if (expendedDropZone) {
        return (
            <div
                onMouseMove={(e) => {
                    setRefresh(!refresh);
                }}
                className="destinationExtendedDropZone"
            >
                <div className="actionBar">
                    <UserfulSearchBar onChange={onDestinationSearchBarChange} />
                </div>
                <div
                    className="zone"
                    ref={setNodeRef}
                    aria-label="Droppable region"
                    id="expended-destinations-drop-zone"
                >
                    {currentDestinations.length <= 0 && existingDestinations.length > 0 ? (
                        <div className="insideWrapper">
                            <div className="text">
                                <div className="mainText">{t("Mapping.NotFound.MainText")}</div>
                                <div className="secondaryText">{t("Mapping.NotFound.SecondaryText")}</div>
                            </div>
                        </div>
                    ) : null}
                    {existingDestinations.length <= 0 ? (
                        <div className="dropZone">
                            <div className="iconWrapper" style={{ marginBottom: "10px" }}>
                                <FontAwesomeIcon icon={faPlus} style={{ width: "16px", height: "16px" }} />
                            </div>
                            <span>{t("CommonUI.MappingGroups.DropDestinationHere")}</span>
                        </div>
                    ) : (
                        <div className="destinationsWrapper">
                            {currentDestinations.map((item, index) => (
                                <DestinationDraggable
                                    key={`${mappingGroupID ? mappingGroupID.value : alert.id.value}-destination-inside-${item.data.id.value}`}
                                    item={item.data}
                                    dragID={`${mappingGroupID ? mappingGroupID.value : alert.id.value}-destination-inside-${item.data.id.value}`}
                                    destinationType={item.destinationType}
                                    currentMappingGroup={mappingGroupID ? mappingGroup : undefined}
                                    onDelete={() => handleRemoveDestination(item.data.id, item.destinationType)}
                                    index={`${index}`}
                                    itemIndex={index}
                                    appID={props.appID}
                                    isMoving={Object.keys(pendingMoveDestinations).includes(item.data.id.value)}
                                    canExecuteMapper={props.canExecuteMapper}
                                    isNotification={props.isNotification}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <DestinationDropInUseConfirmation mappingGroupID={mappingGroupID} />
                {pendingAddDestination && (
                    <MirrorError
                        hideMirrorError={hideMirrorError}
                        confirmMirrorReplace={confirmMirrorReplace}
                        pendingAddDestination={pendingAddDestination}
                    />
                )}
                {destError && <DestError setDestError={setDestError} destError={destError} />}
                {pendingHLSAudioDestination && (
                    <HLSAudioWarning
                        hide={hideHLSAudioError}
                        confirm={confirmHLSAudio}
                        pendingDestination={pendingHLSAudioDestination}
                    />
                )}
                {isDraggingValid && (
                    <div className="dragginDestinationOverlay">
                        <div className="iconWrapper" style={{ marginBottom: "10px" }}>
                            <FontAwesomeIcon icon={faPlus} style={{ width: "16px", height: "16px" }} />
                        </div>
                        <span>{t("CommonUI.MappingGroups.DropDestinationHere")}</span>
                    </div>
                )}
                {isBusy && (
                    <div className="mappingGroupBusy">
                        <FontAwesomeIcon icon={faCircleNotch} spin />
                    </div>
                )}
            </div>
        );
    } else {
        return (
            <div
                onMouseMove={(e) => {
                    setRefresh(!refresh);
                }}
                className={`mapping-groups-body-item-destination-wrapper ${ref.current?.scrollWidth <= ref.current?.clientWidth ? "mapping-groups-body-item-destination-wrapper-no-overflow" : ""}`}
                ref={ref}
            >
                <div className="destinationDropZoneWrapper" id="destinations-drop-zone" ref={setNodeRef}>
                    <div className={`destinationsDropZone`} aria-label="Droppable region">
                        {existingDestinations.map((item, index) => (
                            <DestinationDraggable
                                key={`${mappingGroupID ? mappingGroupID.value : alert.id.value}-destination-${item.data.id.value}`}
                                item={item.data}
                                dragID={`${mappingGroupID ? mappingGroupID.value : alert.id.value}-destination-${item.data.id.value}`}
                                destinationType={item.destinationType}
                                currentMappingGroup={mappingGroupID ? mappingGroup : undefined}
                                onDelete={() => handleRemoveDestination(item.data.id, item.destinationType)}
                                index={`${index}`}
                                itemIndex={index}
                                appID={props.appID}
                                isMoving={Object.keys(pendingMoveDestinations).includes(item.data.id.value)}
                                canExecuteMapper={props.canExecuteMapper}
                                isNotification={props.isNotification}
                            />
                        ))}
                        {existingDestinations.length <= 0 ? (
                            <div className="dropZone">
                                {!isDraggingValid && (
                                    <>
                                        <div className="iconWrapper" style={{ marginBottom: "10px" }}>
                                            <FontAwesomeIcon icon={faPlus} style={{ width: "16px", height: "16px" }} />
                                        </div>
                                        <span>{t("CommonUI.MappingGroups.DropDestinationHere")}</span>
                                    </>
                                )}
                            </div>
                        ) : null}
                    </div>

                    {mappingGroupID && <DestinationDropInUseConfirmation mappingGroupID={mappingGroupID} />}
                    {pendingAddDestination && (
                        <MirrorError
                            hideMirrorError={hideMirrorError}
                            confirmMirrorReplace={confirmMirrorReplace}
                            pendingAddDestination={pendingAddDestination}
                        />
                    )}
                    {pendingHLSAudioDestination && (
                        <HLSAudioWarning
                            hide={hideHLSAudioError}
                            confirm={confirmHLSAudio}
                            pendingDestination={pendingHLSAudioDestination}
                        />
                    )}
                    {destError && <DestError setDestError={setDestError} destError={destError} />}
                </div>
                {isDraggingValid && !isBusy && (
                    <div className="dragginDestinationOverlay">
                        <div className="iconWrapper" style={{ marginBottom: "10px" }}>
                            <FontAwesomeIcon icon={faPlus} style={{ width: "16px", height: "16px" }} />
                        </div>
                        <span>{t("CommonUI.MappingGroups.DropDestinationHere")}</span>
                    </div>
                )}
                {isBusy && (
                    <div className="mappingGroupBusy">
                        <FontAwesomeIcon icon={faCircleNotch} spin />
                    </div>
                )}
            </div>
        );
    }
}
